<template>
  <section class="gf-settings-project-card-static">
    <gf-entity-card :description="description">
      <template #headerTitle>
        <span><i :class="projectCardIcon"></i></span>
        <h6 class="mb-0">
          <a :href="projectLink">
            {{ `${ownerAlias}/${title}` }}
          </a>
        </h6>
        <div
          v-if="cardHeaderLabels"
          class="labels"
        >
          <span
            v-for="(label, index) in cardHeaderLabels"
            :key="index"
            class="project-badge"
          >
            {{ label }}
          </span>
        </div>
      </template>
      <template #actions>
        <button
          v-if="ownerAlias === userAlias"
          class="btn btn-sm btn-danger"
          type="button"
          @click="onProjectDeleteClick"
        >
          {{ $t('message.delete') }}
          <i class="gf-icon gf-icon-trash ml-1"></i>
        </button>
        <button
          v-else-if="canLeaveProject"
          class="btn btn-sm btn-danger"
          type="button"
          @click="onLeaveProjectClick"
        >
          {{ $t('message.leave') }}
          <i class="gf-icon gf-icon-logout ml-1"></i>
        </button>
      </template>
      <template
        v-if="canReportProject"
        #dropdownActions
      >
        <button
          v-if="canReportProject"
          class="dropdown-item f5"
          type="button"
          @click="onReportProjectClick"
        >
          <i class="gf-icon gf-icon-alert mr-1"></i>
          {{ $t('message.report') }}
        </button>
      </template>
      <template #footerContent>
        <div class="gf-settings-project-card-static__footer-content">
          <small v-if="language">
            <span
              class="repo-language-color mr-1"
              :class="`lang-color-${languageColor}`"
            ></span>
            {{ language }}
          </small>
          <small class="text-muted">
            {{ $t('projects.updatedAt', { time: updatedTimeAge }) }}
          </small>
          <span
            v-if="showYouOwnerLabel && userProjectOwner"
            class="gf-owner-badge"
          >
            <i class="gf-icon gf-icon-person mr-1"></i>
            {{ $t('projects.owner.isOwner') }}
          </span>
          <span
            v-if="showOwnerLabel && ownerAliasType.toUpperCase() !== 'USER'"
            :class="ownerBadgeClass"
          >
            <i
              class="gf-icon mr-1"
              :class="ownerIconClass"
            ></i>
            <a
              class="ml-1"
              :href="ownerLinkHref"
            >
              <span class="mr-1">{{ ownerIconText }}</span>
              <span class="owner-alias">{{ ownerAlias }}</span>
            </a>
          </span>
        </div>
      </template>
      <template #mobileFooterActions>
        <div class="gf-settings-project-card-static__footer-actions --mobile">
          <button
            v-if="ownerAlias === userAlias"
            class="btn btn-sm btn-danger"
            type="button"
            @click="onProjectDeleteClick"
          >
            <i class="gf-icon gf-icon-trash"></i>
          </button>
          <button
            v-else-if="canLeaveProject"
            class="btn btn-sm btn-danger"
            type="button"
            @click="onLeaveProjectClick"
          >
            <i class="gf-icon gf-icon-logout ml-1"></i>
          </button>
        </div>
      </template>
    </gf-entity-card>
    <gf-custom-modal-window
      ref="leaveProjectModal"
      class="text-wrap"
      gf-modal-data-name="projectLeave"
      :gf-modal-header="$t('projects.leave.title')"
      gf-modal-wrapper-width="500px"
      @close="resetProjectLeaveErrors"
    >
      <template #body>
        <project-leave-form
          :alias="ownerAlias"
          :project-alias="alias"
          @submit="leaveProject"
        />
      </template>
    </gf-custom-modal-window>
    <gf-custom-modal-window
      ref="reportProjectModal"
      gf-modal-data-name="projectComplaint"
      :gf-modal-header="$t('projects.report.title')"
      gf-modal-wrapper-width="500px"
      @close="resetProjectReportErrors"
    >
      <template #body>
        <report-form
          report-type="project"
          :recipient-name="alias"
          :is-async="true"
          @submit="reportProject"
        />
      </template>
    </gf-custom-modal-window>
    <gf-custom-modal-window
      ref="deleteProjectModal"
      gf-modal-data-name="deleteProject"
      :gf-modal-header="$t('projects.delete.title')"
      gf-modal-wrapper-width="600px"
    >
      <template #body>
        <form
          :action="`/settings/projects/${uuid}/delete`"
          method="post"
          enctype="multipart/form-data"
        >
          <input
            type="hidden"
            name="_csrf"
            :value="csrf"
          />
          <gf-custom-alert variant="warning">
            <template #body>
              <i class="gf-icon gf-icon-warning mr-1"></i>
              {{ $t('message.attentionDescription') }}
            </template>
          </gf-custom-alert>
          <div class="d-flex flex-column gap-3 mt-2">
            <span>
              {{ $t('projects.delete.description', { title: title }) }}
            </span>
            <div class="d-flex flex-column gap-2">
              <span
                v-html="
                  $t('projects.delete.controlString.description', {
                    controlString: `${ownerAlias}/${alias}`
                  })
                "
              ></span>
              <input
                type="hidden"
                :value="uuid"
                name="projectUuid"
              />
              <input
                class="form-control form-control-sm"
                name="controlString"
                type="text"
                required
              />
              <button
                type="submit"
                class="btn btn-danger btn-block btn-sm"
              >
                {{ $t('message.delete') }}
              </button>
            </div>
          </div>
        </form>
      </template>
    </gf-custom-modal-window>
  </section>
</template>

<script>
import GfEntityCard from '@shared/shared-components/shared/GfEntityCard';
import ProjectsMessages from '@/shared-modules/modules/projects/projects.messages';
import ProjectsService from '@shared/modules/projects/projects.service';
import ReportForm from '@shared/shared-components/report-form';
import GfCustomModalWindow from '@/shared-modules/shared-components/shared/GfCustomModalWindow';
import ProjectLeaveForm from '@/shared-modules/modules/project/components/project-leave-form';
import { FORM_NAME_ENUMS } from '@/shared-modules/variables/form-name.enums';
import GfCustomAlert from '@shared/shared-components/shared/GfCustomAlert';

export default {
  name: 'GfSettingsProjectCardStatic',
  components: {
    GfEntityCard,
    ReportForm,
    GfCustomModalWindow,
    ProjectLeaveForm,
    GfCustomAlert
  },
  props: {
    // удалить после подлития топиков
    project: {
      type: Object,
      default() {
        return {};
      }
    },
    uuid: {
      type: String,
      default: null
    },
    alias: {
      type: String,
      default: null
    },
    ownerAliasType: {
      type: String,
      default: null
    },
    description: {
      type: String,
      default: null
    },
    language: {
      type: String,
      default: null
    },
    ownerAlias: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: null
    },
    updatedTimeAge: {
      type: String,
      default: null
    },
    isLeaveProject: {
      type: String,
      default: 'false'
    },
    isReportProject: {
      type: String,
      default: 'false'
    },
    isMirror: {
      type: String,
      default: 'false'
    },
    isPrivate: {
      type: String,
      default: 'false'
    },
    isUserProjectOwner: {
      type: String,
      default: 'false'
    },
    showUserOwnerLabel: {
      type: String,
      default: 'true'
    },
    showEntityOwnerLabel: {
      type: String,
      default: 'true'
    }
  },
  data() {
    return {
      csrf: document.querySelector('[name="_csrf"]')?.content
    };
  },
  computed: {
    isWiki() {
      return this.alias?.endsWith('.wiki');
    },
    canLeaveProject() {
      return this.isLeaveProject === 'true';
    },
    canReportProject() {
      return this.isReportProject === 'true';
    },
    mirror() {
      return this.isMirror === 'true';
    },
    private() {
      return this.isPrivate === 'true';
    },
    userProjectOwner() {
      return this.isUserProjectOwner === 'true';
    },
    showYouOwnerLabel() {
      return this.showUserOwnerLabel === 'true';
    },
    showOwnerLabel() {
      return this.showEntityOwnerLabel === 'true';
    },
    projectCardIcon() {
      return this.isWiki ? 'gf-icon gf-icon-book-open' : 'gf-icon gf-icon-folder';
    },
    cardHeaderLabels() {
      const labels = [];
      if (this.private) labels.push(this.$t('projects.private'));
      if (this.mirror) labels.push(this.$t('projects.mirror'));
      if (this.isWiki) labels.push(this.$t('projects.wiki'));

      return labels;
    },
    projectLink() {
      return `/project/${this.ownerAlias}/${this.alias}`;
    },
    ownerIconClass() {
      let resultClass = '';
      switch (this.ownerAliasType.toUpperCase()) {
        case 'TEAM':
          resultClass = 'gf-icon-teams';
          break;
        case 'COMPANY':
          resultClass = 'gf-icon-companies';
          break;
        default:
          break;
      }
      return resultClass;
    },
    ownerIconText() {
      switch (this.ownerAliasType.toUpperCase()) {
        case 'TEAM':
          return this.$t('projects.owner.inTeam');
        case 'COMPANY':
          return this.$t('projects.owner.inCompany');
        default:
          return '';
      }
    },
    ownerBadgeClass() {
      let resultClass = '';
      switch (this.ownerAliasType.toUpperCase()) {
        case 'TEAM':
          resultClass = 'gf-team-badge';
          break;
        case 'COMPANY':
          resultClass = 'gf-company-badge';
          break;
        default:
          break;
      }
      return resultClass;
    },
    ownerLinkHref() {
      let resultHref = '';
      switch (this.ownerAliasType.toUpperCase()) {
        case 'TEAM':
          resultHref = `/team/${this.ownerAlias}`;
          break;
        case 'COMPANY':
          resultHref = `/company/${this.ownerAlias}`;
          break;
        default:
          break;
      }
      return resultHref;
    },
    userAlias() {
      return this.$store.getters.getUserAlias;
    },
    languageColor() {
      return this.language
        ?.toLowerCase()
        .replaceAll('#', 'sharp')
        .replaceAll('.', '-')
        .replaceAll('+', 'plus')
        .replaceAll(' ', '-');
    }
  },
  methods: {
    onLeaveProjectClick() {
      this.openLeaveProjectModal();
    },
    onReportProjectClick() {
      this.openReportProjectModal();
    },
    onProjectDeleteClick() {
      this.openDeleteProjectModal();
    },
    openLeaveProjectModal() {
      this.$refs.leaveProjectModal.openModalWindow();
    },
    closeLeaveProjectModal() {
      this.$refs.leaveProjectModal.closeModalWindow();
      this.resetProjectLeaveErrors();
    },
    openReportProjectModal() {
      this.$refs.reportProjectModal.openModalWindow();
    },
    closeReportProjectModal() {
      this.$refs.reportProjectModal.closeModalWindow();
      this.resetProjectReportErrors();
    },
    openDeleteProjectModal() {
      this.$refs.deleteProjectModal.openModalWindow();
    },
    closeDeleteProjectModal() {
      this.$refs.deleteProjectModal.closeModalWindow();
    },
    leaveProject(data) {
      ProjectsService.leaveProject(this.ownerAlias, this.alias, {
        projectUuid: this.uuid,
        controlString: data?.controlString
      }).then(() => {
        this.closeLeaveProjectModal();
        window.location.reload();
      });
    },
    reportProject(data) {
      ProjectsService.reportProject(this.ownerAlias, this.alias, data).then(() => {
        this.closeReportProjectModal();
      });
    },
    resetProjectLeaveErrors() {
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS.LEAVE_PROJECT,
        errorResponse: null
      });
    },
    resetProjectReportErrors() {
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS.REPORT_PROJECT,
        errorResponse: null
      });
    }
  },
  i18n: {
    messages: ProjectsMessages
  }
};
</script>

<style lang="scss">
$this: '.gf-settings-project-card-static';

#{$this} {
  height: 100%;

  #{$this}__footer-content {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;

    & > span {
      flex-wrap: wrap;

      & .owner-alias {
        max-width: 120px;
        text-wrap: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
  #{$this}__footer-actions {
    display: flex;
    text-wrap: nowrap;
    gap: 8px;

    & button:not(.btn-danger) {
      & > i {
        font-size: 14px !important;
      }
    }

    &.--mobile {
      & > button:not(.btn-danger) {
        min-height: 32px !important;

        padding: 8px;
        background-color: var(--bg-light);
        border-radius: 6px;

        &:hover {
          background-color: var(--bg-light-selected);
        }
      }
    }

    & button:not(.btn-danger) {
      min-height: 22px;
      padding: 0px 2px;

      &:focus {
        box-shadow: none;
      }
    }
  }
}
</style>
